// src/pages/LogsPage.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import DataTable from '../components/DataTable';
import DateRangePicker from '../components/DateRangePicker';

const LogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    user: '',
    dateFrom: '',
    dateTo: ''
  });
  const [actions, setActions] = useState([]);

  useEffect(() => {
    fetchLogs();
    fetchDistinctActions();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('audit_logs')
        .select(`
          *,
          users:user_id (name, email)
        `)
        .order('created_at', { ascending: false });

      // Appliquer les filtres
      if (filters.action) {
        query = query.eq('action', filters.action);
      }
      if (filters.user) {
        query = query.ilike('user_email', `%${filters.user}%`);
      }
      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      const { data, error } = await query.limit(1000);
      
      if (!error) setLogs(data || []);
    } catch (error) {
      console.error('Erreur chargement logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDistinctActions = async () => {
    const { data } = await supabase
      .from('audit_logs')
      .select('action')
      .order('action');
    
    const uniqueActions = [...new Set(data?.map(item => item.action) || [])];
    setActions(uniqueActions);
  };

  const getActionBadge = (action) => {
    const colors = {
      'login': 'bg-green-100 text-green-800',
      'logout': 'bg-gray-100 text-gray-800',
      'create': 'bg-blue-100 text-blue-800',
      'update': 'bg-yellow-100 text-yellow-800',
      'delete': 'bg-red-100 text-red-800',
      'export': 'bg-purple-100 text-purple-800'
    };
    
    const defaultColor = 'bg-gray-100 text-gray-800';
    const colorKey = Object.keys(colors).find(key => action?.toLowerCase().includes(key));
    
    return colors[colorKey] || defaultColor;
  };

  const formatJSON = (data) => {
    if (!data) return '-';
    return (
      <pre className="text-xs bg-gray-50 p-2 rounded max-w-xs overflow-x-auto">
        {JSON.stringify(data, null, 2)}
      </pre>
    );
  };

  const columns = [
    {
      key: 'created_at',
      label: 'Date/Heure',
      render: (log) => (
        <div className="text-sm">
          <div className="font-medium text-gray-900">
            {new Date(log.created_at).toLocaleDateString('fr-FR')}
          </div>
          <div className="text-gray-500">
            {new Date(log.created_at).toLocaleTimeString('fr-FR')}
          </div>
        </div>
      )
    },
    {
      key: 'user',
      label: 'Utilisateur',
      render: (log) => (
        <div>
          <div className="font-medium text-gray-900">
            {log.users?.name || 'Utilisateur inconnu'}
          </div>
          <div className="text-xs text-gray-500">
            {log.user_email || log.users?.email}
          </div>
        </div>
      )
    },
    {
      key: 'action',
      label: 'Action',
      render: (log) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getActionBadge(log.action)}`}>
          {log.action}
        </span>
      )
    },
    {
      key: 'entity',
      label: 'Entité',
      render: (log) => (
        <div>
          <span className="text-sm font-medium text-gray-700">{log.entity_type || '-'}</span>
          {log.entity_id && (
            <div className="text-xs text-gray-500">{log.entity_id.slice(0, 8)}...</div>
          )}
        </div>
      )
    },
    {
      key: 'details',
      label: 'Détails',
      render: (log) => (
        <div className="text-sm">
          {log.old_data && (
            <details className="mb-1">
              <summary className="text-xs text-gray-600 cursor-pointer">Anciennes valeurs</summary>
              {formatJSON(log.old_data)}
            </details>
          )}
          {log.new_data && (
            <details>
              <summary className="text-xs text-gray-600 cursor-pointer">Nouvelles valeurs</summary>
              {formatJSON(log.new_data)}
            </details>
          )}
        </div>
      )
    },
    {
      key: 'ip',
      label: 'IP',
      render: (log) => (
        <span className="text-xs text-gray-600">{log.ip_address || '-'}</span>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Logs d'audit
          </h1>
          <p className="text-gray-600 text-lg">
            Journal des actions effectuées dans le CRM
          </p>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Filtres</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action
              </label>
              <select
                value={filters.action}
                onChange={(e) => setFilters({...filters, action: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Toutes les actions</option>
                {actions.map(action => (
                  <option key={action} value={action}>{action}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Utilisateur
              </label>
              <input
                type="text"
                value={filters.user}
                onChange={(e) => setFilters({...filters, user: e.target.value})}
                placeholder="Email ou nom"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date début
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date fin
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3">
            <button
              onClick={() => setFilters({ action: '', user: '', dateFrom: '', dateTo: '' })}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Réinitialiser
            </button>
            <button
              onClick={fetchLogs}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Appliquer les filtres
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total logs</p>
                <p className="text-2xl font-bold text-gray-900">{logs.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Créations</p>
                <p className="text-2xl font-bold text-gray-900">
                  {logs.filter(l => l.action.includes('create')).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100 text-yellow-600 mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Modifications</p>
                <p className="text-2xl font-bold text-gray-900">
                  {logs.filter(l => l.action.includes('update')).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100 text-red-600 mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Suppressions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {logs.filter(l => l.action.includes('delete')).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau des logs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                  </svg>
                </div>
                <p className="text-gray-500 text-lg mb-2">Aucun log trouvé</p>
                <p className="text-gray-400">Les actions effectuées apparaîtront ici</p>
              </div>
            ) : (
              <DataTable data={logs} columns={columns} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogsPage;